import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signUpService, uploadFileService } from "../api/services";
import { PasswordInput } from "../components/PasswordInput";
import { useToast } from "../components/Toast";
import { PermissionsEnum } from "../constants/enums";
import { useRequestError } from "../hooks/useRequestError";
import { useAuthStore } from "../stores/authStore";

export const SignupPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { handleRequestError } = useRequestError();
  const { setSessionFromToken, isAuthenticated, hydrated } = useAuthStore();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [role, setRole] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hydrated && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [hydrated, isAuthenticated, navigate]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!role) {
      setError("Please select a role.");
      return;
    }
    if (password.length < 4) {
      setError("Password must be at least 4 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await signUpService({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phoneNumber.trim(),
        email: email.trim(),
        password,
        password_confirm: passwordConfirm,
        role,
      });

      if (files.length > 0) {
        await Promise.all(files.map((file) => uploadFileService(file, "custom")));
      }

      const ok = setSessionFromToken();
      if (!ok) {
        showToast({
          type: "error",
          title: "Access denied",
          message: "This dashboard is only available for doctors and caregivers.",
        });
        return;
      }
      showToast({
        type: "success",
        title: "Account created",
        message: "Welcome to REEVA Care.",
      });
      navigate("/", { replace: true });
    } catch (err) {
      handleRequestError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        <img src="/logo.png" alt="REEVA" className="auth-logo" />
        <h1>Create Your Account</h1>
        <p className="muted center">
          Doctors and caregivers can register for the REEVA dashboard.
        </p>

        <form className="stack gap-md" onSubmit={onSubmit}>
          <label className="field">
            <span>First name</span>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>Last name</span>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>Phone number</span>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="9665xxxxxxxx"
              required
            />
          </label>
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label className="field">
            <span>Password</span>
            <PasswordInput
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              required
            />
          </label>
          <label className="field">
            <span>Confirm password</span>
            <PasswordInput
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              autoComplete="new-password"
              show={showPasswordConfirm}
              onToggleShow={() => setShowPasswordConfirm((v) => !v)}
              required
            />
          </label>
          <label className="field">
            <span>Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
            >
              <option value="">Select role…</option>
              <option value={PermissionsEnum.DOCTOR}>Doctor</option>
              <option value={PermissionsEnum.CARE_GIVER}>Care Giver</option>
            </select>
          </label>
          <label className="field">
            <span>Credentials (optional)</span>
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              multiple
              onChange={(e) =>
                setFiles(Array.from(e.target.files || []))
              }
            />
            {files.length > 0 ? (
              <div className="chip-list" style={{ marginTop: 8 }}>
                {files.map((file) => (
                  <button
                    key={file.name}
                    type="button"
                    className="chip"
                    onClick={() =>
                      setFiles((prev) => prev.filter((f) => f !== file))
                    }
                  >
                    {file.name} ×
                  </button>
                ))}
              </div>
            ) : null}
          </label>

          {error ? <div className="banner error">{error}</div> : null}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Creating account…" : "Sign up"}
          </button>
        </form>

        <p className="center muted">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};
